import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Icon,
  Skeleton,
  Badge,
  Flex,
  Text,
} from '@kognitos/lattice';
import { ConfigFormRow } from '../WebhookTriggerDialog/ConfigFormRow';
import { isSelectedConnectionInactive } from '../../utils/connection-state-utils';
import type { IConnectionSelectRowProps } from './types';

/**
 * Shared connection select row for trigger configure steps.
 *
 * Renders either a "new connection" button (when an integration is selected
 * but has no draft connections and selections are not locked) or the
 * connection dropdown, inside a ConfigFormRow. Translation strings are passed
 * in so the row is namespace-agnostic.
 *
 * Inactive connections stay selectable, with a notice that they will be
 * reactivated.
 *
 * @param props - Component props
 * @returns JSX element containing the connection select row
 */
export function ConnectionSelectRow({
  label,
  description,
  placeholder,
  newConnectionLabel,
  inactiveLabel,
  selectedIntegration,
  selectedConnection,
  onConnectionChange,
  draftConnections,
  isLoading,
  lockSelections = false,
  onAddConnection,
}: IConnectionSelectRowProps) {
  const isInactive = isSelectedConnectionInactive(
    draftConnections,
    selectedConnection,
  );

  return (
    <ConfigFormRow
      label={label}
      description={description}
      disabled={!selectedIntegration}
    >
      {isLoading ? (
        <Skeleton className="h-9 w-full rounded-md bg-alternate-1" />
      ) : selectedIntegration &&
        draftConnections.length === 0 &&
        !lockSelections ? (
        <Button
          variant="outline"
          className="h-9 shadow-xs"
          onClick={onAddConnection}
        >
          <Icon type="Plus" className="size-4" />
          {newConnectionLabel}
        </Button>
      ) : (
        <>
          <Select
            value={selectedConnection}
            onValueChange={onConnectionChange}
            disabled={!selectedIntegration || lockSelections}
          >
            <SelectTrigger className="w-full bg-background">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {draftConnections.map(conn => (
                <SelectItem key={conn.connection_id} value={conn.connection_id}>
                  {conn.connection_name || conn.connection_id}
                  {conn.book_version && (
                    <Badge variant="outline" className="text-muted-foreground">
                      v{conn.book_version}
                    </Badge>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isInactive && (
            <Flex align="start" gap={1.5}>
              <Icon
                type="Power"
                size="xs"
                aria-hidden
                className="mt-0.5 shrink-0 text-muted-foreground"
              />
              <Text level="xSmall" color="muted" role="status">
                {inactiveLabel}
              </Text>
            </Flex>
          )}
        </>
      )}
    </ConfigFormRow>
  );
}
